import { useNavigate } from 'react-router-dom';
import { Building2, Users, TrendingUp, BarChart3, Loader2 } from 'lucide-react';
import { usePlatformAnalytics } from '@/hooks/usePlatformAnalytics';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RevenueDistributionChart, AiAdoptionChart, EmployeeCountChart, RevenuePotentialChart } from '@/components/charts';
import TopProspectsTable from '@/components/TopProspectsTable';

export default function AnalyticsDashboard() {
  const navigate = useNavigate();

  // Get user from localStorage
  const userStr = localStorage.getItem('user');
  const currentUser = userStr ? JSON.parse(userStr) : null;

  // Fetch platform analytics
  const { data, loading, error, refetch } = usePlatformAnalytics();

  // Check authentication and authorization
  if (!currentUser || currentUser.role !== 'super_admin') {
    navigate('/dashboard');
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive">Error Loading Analytics</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={refetch} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle>No Analytics Data</CardTitle>
              <CardDescription>Unable to load analytics data at this time.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  const { summary, revenueDistribution, aiAdoptionDistribution, employeeDistribution, companyScatterData } = data;

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Company Analytics</h1>
          <p className="text-muted-foreground mt-2">
            Platform-wide analytics and company insights
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Companies */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Companies</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{summary.totalCompanies}</div>
              <p className="text-xs text-muted-foreground mt-1">Registered companies</p>
            </CardContent>
          </Card>

          {/* Total Employees */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Employees</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{summary.totalEmployees.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground mt-1">Across all companies</p>
            </CardContent>
          </Card>

          {/* Total Users */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{summary.totalUsers}</div>
              <p className="text-xs text-muted-foreground mt-1">Platform users</p>
            </CardContent>
          </Card>

          {/* Average Revenue Score */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Revenue Score</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{summary.avgRevenueScore}</div>
              <p className="text-xs text-muted-foreground mt-1">Platform average</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue Distribution */}
          <RevenueDistributionChart data={revenueDistribution} />

          {/* AI Adoption Distribution */}
          <AiAdoptionChart data={aiAdoptionDistribution} />
        </div>

        {/* Employee Distribution */}
        <EmployeeCountChart data={employeeDistribution} />

        {/* Revenue Potential Scatter Plot */}
        {companyScatterData && companyScatterData.length > 0 && (
          <RevenuePotentialChart data={companyScatterData} />
        )}

        {/* Top Prospects Table */}
        {companyScatterData && companyScatterData.length > 0 && (
          <TopProspectsTable data={companyScatterData} maxRows={10} />
        )}
      </div>
    </div>
  );
}
